// React Query hooks for org-scoped managed contract CRUD — list, create, update, expiring, and recent.

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { managedContractsApi } from "../services/api";
import type {
  CreateManagedContractInput,
  UpdateManagedContractInput,
} from "../types";

export function useManagedContracts(params?: {
  search?: string;
  type?: string;
  status?: string;
  supplierId?: string;
  contractTypeId?: string;
  businessUnitId?: string;
  expiryWithinDays?: number;
  limit?: number;
  nextToken?: string;
}) {
  return useQuery({
    queryKey: ["managed-contracts", params],
    queryFn: () => managedContractsApi.list(params),
  });
}

// Contract Register full-drain hook: no `limit`, so the backend returns the
// {items, summary, complete, lastComputedAt, summaryScope} envelope with an
// optional server-side grade filter. Distinct query key from the limited
// callers so the two shapes never collide in cache.
export function useContractRegister(params?: {
  search?: string;
  type?: string;
  status?: string;
  supplierId?: string;
  contractTypeId?: string;
  businessUnitId?: string;
  grade?: string;
}) {
  return useQuery({
    queryKey: ["contract-register", params],
    queryFn: () => managedContractsApi.listRegister(params),
  });
}

export function useExpiringContracts(days: number) {
  return useQuery({
    queryKey: ["managed-contracts", "expiring", days],
    queryFn: () => managedContractsApi.list({ expiryWithinDays: days }),
  });
}

export function useRecentManagedContracts(limit: number) {
  return useQuery({
    queryKey: ["managed-contracts", "recent", limit],
    queryFn: () => managedContractsApi.list({ limit }),
  });
}

export function useManagedContract(id: string) {
  return useQuery({
    queryKey: ["managed-contracts", id],
    queryFn: () => managedContractsApi.get(id),
    enabled: !!id,
  });
}

export function useCreateManagedContract() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: CreateManagedContractInput) =>
      managedContractsApi.create(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["managed-contracts"] });
    },
  });
}

export function useUpdateManagedContract() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      input,
    }: {
      id: string;
      input: UpdateManagedContractInput;
    }) => managedContractsApi.update(id, input),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["managed-contracts", variables.id],
      });
      queryClient.invalidateQueries({ queryKey: ["managed-contracts"] });
    },
  });
}
